<template>
  <div class="height-100-p">
    <el-form :model="editForm" :rules="editFormRules" ref="editForm">
      <el-table
        :data="barcodeUser.list"
        v-loading="barcodeUser.loadingSearch"
        id="barCodeUser-data-list"
        size="mini"
        tooltip-effect="dark"
        sortable
        class="dark-blue-table"
        :empty-text="$t('common.notAbleToFindRecords')"
        @sort-change="handleSortChange"
        stripe
      >
        <el-table-column
          prop="userId"
          :label="$t('barCodeUserType.userId')"
          :min-width="5"
          align="center"
          sortable="custom"
        ></el-table-column>
        <el-table-column
          prop="pin"
          :label="$t('barCodeUserType.pin')"
          :min-width="5"
          align="center"
          sortable="custom">
          <template slot-scope="scope">
            <template v-if="scope.row.edit">
              <el-form-item prop="pin">
                <el-input
                  v-model="editForm.pin"
                  v-numeric-validation
                  maxlength="5"
                  class="inline-input"
                  id="row-form-pin"
                  ref="row-form-pin"></el-input>
              </el-form-item>
            </template>
            <template v-else>
              {{ scope.row.pin }}
            </template>
          </template>
        </el-table-column>
        <el-table-column
          prop="fullName"
          :label="$t('barCodeUserType.fullName')"
          :min-width="5"
          align="center"
          sortable="custom"
        >
          <template slot-scope="scope">
            <template v-if="scope.row.edit">
              <el-form-item prop="fullName">
                <el-input
                  v-model="editForm.fullName"
                  v-alphanumeric-validation
                  maxlength="100"
                  class="inline-input"
                  id="row-form-fullName"
                  ref="row-form-fullName"></el-input>
              </el-form-item>
            </template>
            <template v-else>
              <el-tooltip class="item" effect="dark" :content="scope.row.fullName" placement="top">
                <b>{{ scope.row.fullName }}</b>
              </el-tooltip>
            </template>
          </template>
        </el-table-column>
        <el-table-column
          prop="role"
          :label="$t('barCodeUserType.role')"
          :min-width="5"
          align="center"
          sortable="custom">
          <template slot-scope="scope">
            <template v-if="scope.row.edit">
              <el-form-item prop="role">
                <el-select v-model="editForm.role" class="inline-input" size="mini">
                  <el-option
                    v-for="item in roleOptions"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"></el-option>
                </el-select>
              </el-form-item>
            </template>
            <template v-else>
              {{ getRoleName(scope.row.role) }}
            </template>
          </template>
        </el-table-column>
        <el-table-column
          prop="warehouseCode"
          :label="$t('barCodeUserType.warehouseCode')"
          :min-width="5"
          align="center"
          sortable="custom">
          <template slot-scope="scope">
            <template v-if="scope.row.edit">
              <el-form-item prop="warehouseCode">
                <el-select v-model="editForm.warehouseId" class="inline-input" size="mini">
                  <el-option
                    v-for="item in warehouseOptions"
                    :key="item.id"
                    :label="item.code"
                    :value="item.id"></el-option>
                </el-select>
              </el-form-item>
            </template>
            <template v-else>
              {{ scope.row.warehouseCode }}
            </template>
          </template>
        </el-table-column>
        <el-table-column
            prop="company"
            :label="$t('barCodeUserType.company')"
            :min-width="5"
            align="center"
            sortable="custom">
          <template slot-scope="scope">
            <template v-if="scope.row.edit">
              <el-form-item prop="company">
                <el-input
                    v-model="editForm.company"
                    v-alphanumeric-validation
                    maxlength="50"
                    class="inline-input"
                    id="row-form-company"
                    ref="row-form-company"></el-input>
              </el-form-item>
            </template>
            <template v-else>
              {{ scope.row.company }}
            </template>
          </template>
        </el-table-column>
        <el-table-column
          prop="active"
          :label="$t('barCodeUserType.active')"
          :min-width="5"
          align="center"
          sortable="custom">
          <template slot-scope="scope">
            <i v-if="scope.row.active" class="el-icon-success green-success"></i>
          </template>
        </el-table-column>
        <el-table-column
          prop="options"
          :label="$t('barCodeUserType.options')"
          :min-width="4"
          align="center">
          <template slot-scope="scope">
            <template v-if="scope.row.active">
              <el-tooltip
                v-if="showEditButton(scope.row)"
                class="item"
                effect="dark"
                :enterable="false"
                :content="$t('barCodeUserType.edit')"
                placement="top-start">
                <i class="el-icon-edit icon-action" @click="handleEdit(scope.row)"></i>
              </el-tooltip>
              <template v-else-if="scope.row.edit">
                <el-button icon="el-icon-success" circle size="mini" @click="handleUpdate()"></el-button>
                <el-button icon="el-icon-circle-close" circle size="mini" @click="cancelEdit(scope.row)"></el-button>
              </template>
              <template v-if="!scope.row.edit">
                <el-tooltip
                  class="item"
                  effect="dark"
                  :content="$t('warehouse.orderEntry.delete')"
                  placement="top-start">
                  <i v-if="showDeleteButton()"
                    class="el-icon-delete icon-action"
                    type="primary"
                    @click="handleDelete(scope.row.id, scope.row.userId,scope.row.fullName)"></i>
                </el-tooltip>
              </template>
            </template>
            <template v-else-if="canReactivateUser()">
              <el-tooltip
                class="item"
                effect="dark"
                :content="$t('common.reactivate')"
                placement="top-start">
                <i class="el-icon-refresh-left icon-action" @click="confirmationReactivateMessage(scope.row)" />
              </el-tooltip>
            </template>
          </template>
        </el-table-column>
      </el-table>
    </el-form>
    <div class="pagination-footer" v-show="barcodeUser.total > 0">
      <span class="el-pagination__total">
        <span class="el-pagination__title">{{$t("barCodeUserType.total")}}</span>
        {{ barcodeUser.total }}
      </span>
      <el-pagination
        @size-change="handleSearchChangeLimit"
        :page-sizes="sizes"
        :page-size="barcodeUser.actualFilters.rows"
        :current-page.sync="barcodeUser.actualFilters.page"
        @current-change="handleSearchChangePage"
        :total="barcodeUser.total"
        layout="slot, sizes, prev, pager, next, jumper"></el-pagination>
    </div>
  </div>
</template>
<script>
import { Message } from 'element-ui';
import { mapGetters } from 'vuex';
import constants from '@/utils/constants';

const barcodeUserActionSearchStr = 'barcodeUser/search';

export default {
  name: 'SearchSummary',
  computed: {
    ...mapGetters(['barcodeUser'])
  },
  data() {
    const validPin = (rule, value, callback) => {
      if (this.editForm.pin <= 0) {
        callback(new Error(this.$t('barCodeUserType.error.pinNotZero')));
      } else {
        callback();
      }
    };
    return {
      editForm: {
        id: null,
        userId: null,
        pin: null,
        fullName: null,
        role: null,
        warehouseId: null,
        company: null
      },
      editFormRules: {
        pin: [
          {
            required: true,
            message: this.$t('barCodeUserType.error.pinRequired'),
            trigger: 'blur'
          },
          { validator: validPin, trigger: 'blur' }
        ],
        fullName: [
          {
            required: true,
            message: this.$t('barCodeUserType.error.fullNameRequired'),
            trigger: 'blur'
          }
        ],
        role: [
          {
            required: true,
            message: this.$t('barCodeUserType.error.roleRequired'),
            trigger: 'blur'
          }
        ],
        warehouseId: [
          {
            required: true,
            message: this.$t('barCodeUserType.error.warehouseIdRequired'),
            trigger: 'blur'
          }
        ],
        company: [
          {
            required: false,
            trigger: 'blur'
          }
        ]
      },
      roleOptions: constants.BAR_CODE_USER.ROLE_OPTIONS_ADD,
      sizes: constants.TABLES.DEFAULT_LIMIT_SIZES,
      warehouseOptions: []
    };
  },
  methods: {
    handleSortChange({ prop, order }) {
      this.$store.dispatch(barcodeUserActionSearchStr, {
        orderField: prop,
        orderDirection: constants.TABLES.ORDER_DIRECTION[order]
      });
    },
    handleSearchChangePage(val) {
      this.$store.dispatch(barcodeUserActionSearchStr, { page: val });
    },
    handleSearchChangeLimit(val) {
      this.$store.dispatch(barcodeUserActionSearchStr, {
        rows: val,
        page: constants.TABLES.DEFAULT_PAGE
      });
    },
    getRoleName(key) {
      const selectedRole = this.roleOptions.find(role => role.value === key);
      return selectedRole.label;
    },
    handleEdit(row) {
      this.$store.dispatch('barcodeUser/setEditingRow', true);
      row.edit = true;
      this.editForm.id = row.id;
      this.editForm.userId = row.userId;
      this.editForm.pin = row.pin;
      this.editForm.fullName = row.fullName;
      this.editForm.role = row.role;
      this.editForm.warehouseId = row.warehouseId;
      this.editForm.company = row.company;
    },
    clearEdit() {
      this.$store.dispatch('barcodeUser/setEditingRow', false);
      this.$refs.editForm.resetFields();
    },
    cancelEdit(row) {
      row.edit = false;
      this.clearEdit();
    },
    handleUpdate() {
      this.$refs.editForm.validate((valid, validationsErrors) => {
        if (valid) {
          Message.closeAll();
          const loading = this.$loading(constants.LOADING.DEFAULT_CONFIG);
          this.$store
            .dispatch('barcodeUser/update', {
              scannerUserId: this.editForm.id,
              barcodeUserToUpdate: this.editForm
            })
            .then((response) => {
              loading.close();
              this.$message.success(response.message);
              this.clearEdit();
              this.$store.dispatch(barcodeUserActionSearchStr, {});
            })
            .catch(() => {
              loading.close();
            });
        } else {
          let errorMessage = '';
          Object.keys(validationsErrors).forEach((key) => {
            validationsErrors[key].forEach((validation) => {
              errorMessage += `${validation.message}. `;
            });
          });
          this.$message.warning(errorMessage);
        }
      });
    },
    showEditButton() {
      return (
        this.$can(this.permissions.WAREHOUSE.EDIT_BARCODE_USER)
        && !this.barcodeUser.isEditingRow
      );
    },
    showDeleteButton() {
      return this.$can(this.permissions.WAREHOUSE.EDIT_BARCODE_USER);
    },
    handleDelete(id, userId, fullName) {
      let message = this.$t('barCodeUserType.unitDeleteConfirmation');
      message = message.replace('[fullName]', fullName).replace('[id]', userId);
      this.$confirm(message, { confirmButtonText: this.$t('common.yes') })
        .then(() => {
          this.handleDeleteAction(id);
        })
        .catch(() => {});
    },
    handleDeleteAction(id) {
      const loading = this.$loading(constants.LOADING.DEFAULT_CONFIG);
      this.$store
        .dispatch('barcodeUser/delete', id)
        .then((response) => {
          loading.close();
          this.$message.success(response.message);
          this.$store.dispatch(barcodeUserActionSearchStr, {});
        })
        .catch((error) => {
          loading.close();
          this.$message({
            showClose: true,
            message: error.response.data.message,
            type: 'error',
            duration: constants.MESSAGES.LONG_DURATION
          });
        });
    },
    canReactivateUser() {
      return this.$can(this.permissions.WAREHOUSE.REACTIVATE_BARCODE_USER);
    },
    async confirmationReactivateMessage(row) {
      let message = this.$t('warehouse.orderEntry.reactivateBarcodeUserConfirmation');
      message = message.replace('[id]', row.userId);
      message = message.replace('[pin]', row.pin);
      message = message.replace('[fullName]', row.fullName);
      const confirm = await this.$confirm(message, { confirmButtonText: this.$t('common.yes'), dangerouslyUseHTMLString: true }).catch(() => false);
      if (confirm) {
        await this.handleReactivate(row.id);
      }
    },
    async handleReactivate(id) {
      const loading = this.$loading(constants.LOADING.DEFAULT_CONFIG);
      try {
        const response = await this.$store.dispatch('barcodeUser/reactivate', id);
        await this.$store.dispatch(barcodeUserActionSearchStr, {});
        this.$message.success(response.message);
      } finally {
        loading.close();
      }
    }
  },
  mounted() {
    this.$store.dispatch('warehouse/getWarehouseCodes').then(({ data }) => {
      this.warehouseOptions = data;
    });
  }
};
</script>
